import portrait1 from "@/assets/images/users/portrait-1.png";
import portrait2 from "@/assets/images/users/portrait-2.png";
import portrait3 from "@/assets/images/users/portrait-3.png";
import portrait4 from "@/assets/images/users/portrait-4.png";
import portrait5 from "@/assets/images/users/portrait-5.png";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import useUserStore from "@/store/userStore";
import { getLoginURL } from "@/utils/auth";
import { Link } from "@tanstack/react-router";
import { FC } from "react";
import { useTranslation } from "react-i18next";

const CreateCircleCard: FC<{
  children: React.ReactNode;
  image?: string | null;
}> = ({ children, image }) => {
  const { token } = useUserStore();
  const { t } = useTranslation();
  return image ? (
    <div className="group relative flex min-h-[476px] flex-1 flex-col justify-end overflow-hidden rounded-2xl p-4 text-white sm:h-[520px] md:p-6">
      <div
        className="absolute inset-0 z-0 scale-100 transition-transform duration-500 ease-in-out group-hover:scale-110"
        style={{
          background: `linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), url(${image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="relative z-10">
        {children}
        <Button size="lg" className="w-fit" asChild>
          <Link
            to={token ? "/circle-creation" : getLoginURL("/circle-creation")}
            search={{ source: "coh" }}
          >
            {t("createCircle.button")}
          </Link>
        </Button>
      </div>
    </div>
  ) : (
    <div
      className="relative flex flex-1 flex-col overflow-hidden rounded-2xl bg-[--neutrals-gray-03] p-4 md:p-6"
      id="joyride-start-circle"
    >
      {/* Decorative cropped circles */}
      <Circle className="scale-[1.3]" />
      <Circle className="scale-[2.3] border-[0.5px]" />
      <Circle className="scale-[3.4] border-[0.2px]" />
      {/* Floating avatars */}
      <ul className="relative  min-h-[240px] flex-1 ">
        <li className="absolute start-[4%] top-[4%] animate-floatOrbit  md:start-[12%] md:top-[3%]">
          <Avatar image={portrait1} points={600} />
        </li>
        <li
          className="absolute start-[60%] top-[4%] animate-floatOrbit  md:start-[58%] md:top-[3%]"
          style={{ animationDelay: "1s" }}
        >
          <Avatar image={portrait2} points={250} />
        </li>
        <li
          className="absolute start-[40%] top-[27%] animate-floatOrbit  md:start-[42%] md:top-[28%]"
          style={{ animationDelay: "2s" }}
        >
          <Avatar image={portrait3} points={120} />
        </li>
        <li
          className="absolute start-[63%] top-[50%] animate-floatOrbit md:start-[66%] md:top-[52%]"
          style={{ animationDelay: "3s" }}
        >
          <Avatar image={portrait4} points={400} />
        </li>
        <li
          className="absolute start-[35%] top-[70%] animate-floatOrbit  md:start-[36%] md:top-[75%]"
          style={{ animationDelay: "4s" }}
        >
          <Avatar image={portrait5} points={500} />
        </li>
      </ul>
      {/* Content */}
      <div className="relative mt-auto ">
        {children}
        <Button size="lg" className="w-fit" asChild>
          <Link
            to={token ? "/circle-creation" : getLoginURL("/circle-creation")}
            search={{ source: "coh" }}
          >
            {t("createCircle.button")}
          </Link>
        </Button>
      </div>
    </div>
  );
};

interface AvatarProps {
  image: string;
  points: number;
  className?: string;
}

function Avatar({ image, points, className }: Readonly<AvatarProps>) {
  const { t } = useTranslation();
  return (
    <div className={cn("flex items-center gap-2 rounded-full bg-white p-0.5 pe-3", className)}>
      <img src={image} alt="Person Avatar" className="size-9 rounded-full" />
      <p className="text-sm">{t("pointsLabel", { count: points })}</p>
    </div>
  );
}

interface CircleProps {
  className?: string;
}
function Circle({ className }: Readonly<CircleProps>) {
  return (
    <div
      className={cn(
        "absolute end-0 top-0 aspect-square w-[36%] translate-x-[18%] translate-y-[-44%] rounded-full border border-[--neutrals-gray-05] opacity-50",
        className,
      )}
    />
  );
}
export default CreateCircleCard;
